<script setup>
import Card from '@/Components/Card/Card.vue';
import { inject } from 'vue';
import ACTION_TYPES from '@/Common/Enum/ActionType.js';
import { API_STATUS } from '@/Common/Enum/ApiStatus.js';
import LinkDomainAnalysis from '@/Pages/DomainScore/Partials/DetailedReport/LinkDomainAnalysis.vue';
import DetailedReportHeader from '@/Pages/DomainScore/Partials/DetailedReport/DetailedReportHeader.vue';
import ExplainScorePart from '@/Pages/DomainScore/Partials/DetailedReport/ExplainScorePart.vue';
import { getDmarcDnsRecordStatus } from '@/Common/DnsRecordMessaging.js';
import ContactUsForAssistance from '@/Pages/DomainScore/Partials/ContactUsForAssistance.vue';

const result = inject('score');

const tags =
  result.records.dmarc?.tags?.reduce((acc, tag) => {
    acc[tag.msg] = tag;
    return acc;
  }, {}) ?? {};

const dnsRecordStatus = getDmarcDnsRecordStatus(result);

const providers = result.records.dmarc.dmarc_providers;
</script>

<template>
  <Card class="bg-white-opacity">
    <DetailedReportHeader
      title="DMARC"
      :description="
        $t(
          'domain_score.partials.detailed_report.impersonation.dmarc_card.dmarc_description'
        )
      "
      :tooltip="
        $t(
          'domain_score.partials.detailed_report.impersonation.dmarc_card.dmarc_tooltip'
        )
      "
    />
    <div class="mt-6 flex flex-col">
      <ExplainScorePart
        :title="
          $t(
            'domain_score.partials.detailed_report.impersonation.dmarc_card.dns_record'
          )
        "
        :action-type="dnsRecordStatus.actionType"
      >
        {{ dnsRecordStatus.message }} -
        <LinkDomainAnalysis :domain="result.domain" fragment="dmarc" />
      </ExplainScorePart>
      <div
        v-if="result.records?.dmarc?.status !== API_STATUS.FATAL"
        class="flex flex-col"
      >
        <ExplainScorePart
          v-if="tags.hasOwnProperty('p=none')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.policy'
            )
          "
          :action-type="ACTION_TYPES.ACTION_REQUIRED"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.policy_set_to_none'
            )
          }}
        </ExplainScorePart>
        <ExplainScorePart
          v-else-if="tags.hasOwnProperty('p=quarantine')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.policy'
            )
          "
          :action-type="ACTION_TYPES.ACTION_REQUIRED"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.policy_set_to_quarantine'
            )
          }}
        </ExplainScorePart>
        <ExplainScorePart
          v-else-if="tags.hasOwnProperty('p=reject')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.policy'
            )
          "
          :action-type="ACTION_TYPES.NO_ACTION"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.policy_set_to_reject'
            )
          }}
        </ExplainScorePart>

        <ExplainScorePart
          v-if="tags.hasOwnProperty('sp=none')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.subdomain_policy'
            )
          "
          :action-type="ACTION_TYPES.ACTION_REQUIRED"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.subdomain_policy_set_to_none'
            )
          }}
        </ExplainScorePart>
        <ExplainScorePart
          v-else-if="tags.hasOwnProperty('sp=quarantine')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.subdomain_policy'
            )
          "
          :action-type="ACTION_TYPES.ACTION_REQUIRED"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.subdomain_policy_set_to_quarantine'
            )
          }}
        </ExplainScorePart>
        <ExplainScorePart
          v-else-if="tags.hasOwnProperty('sp=reject')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.subdomain_policy'
            )
          "
          :action-type="ACTION_TYPES.NO_ACTION"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.subdomain_policy_set_to_reject'
            )
          }}
        </ExplainScorePart>

        <ExplainScorePart
          v-if="tags.hasOwnProperty('pct=0')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.percentage'
            )
          "
          :action-type="ACTION_TYPES.ACTION_REQUIRED"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.policy_is_applied_to_none'
            )
          }}
        </ExplainScorePart>
        <ExplainScorePart
          v-else-if="tags.hasOwnProperty('pct<100')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.percentage'
            )
          "
          :action-type="ACTION_TYPES.ACTION_REQUIRED"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.policy_applied_to_percentage',
              { percentage: result.records.dmarc.policy_percentage }
            )
          }}
        </ExplainScorePart>
        <ExplainScorePart
          v-else
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.percentage'
            )
          "
          :action-type="ACTION_TYPES.NO_ACTION"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.policy_is_applied_to_all'
            )
          }}
        </ExplainScorePart>

        <ExplainScorePart
          v-if="tags.hasOwnProperty('rua=[]')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.aggregate_reporting'
            )
          "
          :action-type="ACTION_TYPES.INFO"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.reporting_address_not_specified'
            )
          }}
        </ExplainScorePart>
        <ExplainScorePart
          v-if="
            result.records.dmarc.aggregate_reporting_addresses.some((email) =>
              email.toLowerCase().includes(result.domain)
            )
          "
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.aggregate_reporting'
            )
          "
          :action-type="ACTION_TYPES.ACTION_RECOMMENDED"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.reporting_address_set_to_self_warning',
              {
                addresses:
                  result.records.dmarc.aggregate_reporting_addresses.join(', ')
              }
            )
          }}
        </ExplainScorePart>
        <ExplainScorePart
          v-else-if="tags.hasOwnProperty('rua=[*]')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.aggregate_reporting'
            )
          "
          :action-type="ACTION_TYPES.NO_ACTION"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.reporting_address_set_to',
              {
                addresses:
                  result.records.dmarc.aggregate_reporting_addresses.join(', ')
              }
            )
          }}
        </ExplainScorePart>

        <ExplainScorePart
          v-if="tags.hasOwnProperty('ruf=[]')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.failure_reporting'
            )
          "
          :action-type="ACTION_TYPES.INFO"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.reporting_address_not_specified'
            )
          }}
        </ExplainScorePart>
        <ExplainScorePart
          v-if="
            result.records.dmarc.failure_reporting_addresses.some((email) =>
              email.toLowerCase().includes(result.domain)
            )
          "
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.failure_reporting'
            )
          "
          :action-type="ACTION_TYPES.ACTION_RECOMMENDED"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.reporting_address_set_to_self_warning',
              {
                addresses:
                  result.records.dmarc.failure_reporting_addresses.join(', ')
              }
            )
          }}
        </ExplainScorePart>

        <ExplainScorePart
          v-else-if="tags.hasOwnProperty('ruf=[*]')"
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.failure_reporting'
            )
          "
          :action-type="ACTION_TYPES.NO_ACTION"
        >
          {{
            $t(
              'domain_score.partials.detailed_report.impersonation.dmarc_card.reporting_address_set_to',
              {
                addresses:
                  result.records.dmarc.failure_reporting_addresses.join(', ')
              }
            )
          }}
        </ExplainScorePart>
      </div>
      <ExplainScorePart
        v-if="providers.length"
        class="mt-2"
        :title="
          $t(
            'domain_score.partials.detailed_report.impersonation.dmarc_card.providers'
          )
        "
        :action-type="ACTION_TYPES.INFO"
      >
        {{ providers.join(', ') }}
      </ExplainScorePart>
      <ExplainScorePart
        v-else
        class="mt-2"
        :title="
          $t(
            'domain_score.partials.detailed_report.impersonation.dmarc_card.providers'
          )
        "
        :action-type="ACTION_TYPES.INFO"
      >
        <ContactUsForAssistance />
      </ExplainScorePart>
    </div>
  </Card>
</template>
